import { Container, Image, TextBalancer } from 'components/common';
import {
  approxCaloriesPerDay,
  approxCaloriesPerMeal,
  approxWeightLoss,
  formatNumberWithCommas,
} from './utils';

interface TDEECalculationResultProps {
  tdee: number;
}

const TDEECalculationResult = ({
  tdee,
}: TDEECalculationResultProps) => {
  const caloriesPerDay = approxCaloriesPerDay(tdee);
  const caloriesPerMeal = approxCaloriesPerMeal(tdee);
  const weightLoss = approxWeightLoss(tdee);

  const formattedTdee = formatNumberWithCommas(tdee);
  const formattedCaloriesPerDay = {
    from: formatNumberWithCommas(caloriesPerDay.from),
    to: formatNumberWithCommas(caloriesPerDay.to),
  };

  return (
    <Container className="tw-py-0">
      <div className="tw-py-16 sm:tw-py-20 tw-grid lg:tw-grid-cols-2 xl:tw-gap-x-8">
        <div className="tw-flex tw-flex-col tw-justify-center tw-pb-8 md:tw-pt-16 md:pb-0 xl:tw-py-24">
          <TextBalancer
            as="h1"
            className="tw-text-[3rem] md:tw-text-[3.5rem] lg:tw-text-[4rem] tw-font-bold tw-mb-8 md:tw-mb-12"
          >
            You “use” approx.{' '}
            <span className="tw-text-primary">
              {formattedTdee} calories <em>(kcal)</em>
            </span>{' '}
            per day!
          </TextBalancer>
          <TextBalancer className="tw-mb-8">
            To lose weight, aim to “eat” around{' '}
            <strong>
              {formattedCaloriesPerDay.from}-
              {formattedCaloriesPerDay.to} kcal per day
            </strong>{' '}
            from food & drinks! You can spread these calories across 3
            meals in a day<sub>1</sub>. If you stick to this for 2
            weeks, you’ll stand to lose between{' '}
            <strong>
              {weightLoss.from}kg & {weightLoss.to}kg
            </strong>
            <sub>2</sub> after two weeks!
          </TextBalancer>
          <TextBalancer className="tw-text-base tw-mb-2">
            <sub>1</sub>Not all 3 meals must be Yummy Bros meal prep.
            You can eat out for one meal! Just remember to Google for
            that meal’s calorie count and keep track of your total
            daily calorie intake. It won’t be very accurate but it’s
            better than nothing!
          </TextBalancer>
          <TextBalancer className="tw-text-base tw-mb-0">
            <sub>2</sub>Note: This will vary depending on your body
            type, genetics, gender, and activity level.
          </TextBalancer>
        </div>
        <div className="tw-flex tw-items-center tw-justify-center items-center tw-relative tw-pt-0 md:tw-pb-16 xl:tw-py-24">
          <div className="tw-block tw-w-full tw-h-full">
            <Image
              src="/assets/images/weight-loss-tips/kcal-frame.png"
              alt="kcal frame"
              width={990}
              height={600}
              layout="responsive"
              priority
            />
          </div>
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-absolute tw-max-w-2xl">
            <p className="tw-uppercase text-muted tw-m-0">
              Your food intake target
            </p>
            <h1 className="tw-m-0 tw-text-primary tw-text-[3rem] md:tw-text-[3.5rem] lg:tw-text-[4rem] tw-font-bold">
              {caloriesPerMeal.from}-{caloriesPerMeal.to} kcal
            </h1>
            <p className="text-[#8e8e8e] tw-m-0 tw-mt-1">
              per meal - based on 3 meals a day
            </p>
          </div>
        </div>
      </div>
      <hr className="tw-m-0" />
    </Container>
  );
};

export default TDEECalculationResult;
